import type { ReactNode, JSX} from "react";
import { cn } from "./cn";
import { Icon } from "./Icon";
import { Row } from "./Row";

export type CountBadgeColor = "blue" | "green" | "orange" | "red" | "purple" | "amber";

export interface EntitySectionProps {
  /** Leading icon/emoji element */
  readonly icon: ReactNode;
  /** Section title */
  readonly title: string;
  /** Count badge (number or text like "3 active") */
  readonly count?: number | string;
  /** Color of the count badge */
  readonly countColor?: CountBadgeColor;
  /** "Show more" label — hidden if undefined */
  readonly showMoreLabel?: string;
  /** Called when "Show more" is clicked */
  readonly onShowMore?: () => void;
  /** Extra className */
  readonly className?: string;
  /** Section content (cards, rows, etc.) */
  readonly children: ReactNode;
}

const countColorMap: Readonly<Record<CountBadgeColor, string>> = {
  blue: "bg-blue/20 text-blue",
  green: "bg-green/20 text-green",
  orange: "bg-accent/20 text-accent",
  amber: "bg-amber/20 text-amber",
  red: "bg-red/20 text-red",
  purple: "bg-purple/20 text-purple",
};

/**
 * A titled content section for entity detail pages.
 * Renders a header row (icon + title + count badge) followed by child content.
 * Optional "Show more" link at the bottom.
 *
 * Generic — used for Meetings, Tasks, Triggers, Notes, etc.
 */
export function EntitySection({
  icon,
  title,
  count,
  countColor = "blue",
  showMoreLabel,
  onShowMore,
  className,
  children,
}: EntitySectionProps): JSX.Element {
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {/* Section header */}
      <div className="flex items-center justify-between">
        <Row gap={2} align="center">
          <span className="inline-flex items-center justify-center shrink-0">
            {icon}
          </span>
          <span className="text-[15px] font-semibold text-content">{title}</span>
        </Row>
        {count !== undefined ? (
          <span
            className={cn(
              "inline-flex items-center justify-center px-2 py-[3px] rounded-[10px] text-[11px] font-semibold",
              countColorMap[countColor],
            )}
          >
            {count}
          </span>
        ) : null}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-2">{children}</div>

      {/* Show more */}
      {showMoreLabel ? (
        <button
          type="button"
          className="mx-auto flex items-center gap-1 text-xs text-content-tertiary bg-transparent border-none
                     cursor-pointer hover:text-content-secondary transition-colors py-1"
          onClick={onShowMore}
        >
          {showMoreLabel}
          <Icon name="chevron-down" size={12} />
        </button>
      ) : null}
    </div>
  );
}
